import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import { fmtSec as fmtSecUTC } from '../services/dates';
import './CalendarPage.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAY_NAMES   = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

const PRIORITIES = [
  { key:'baja',  label:'Baja',  color:'cal-pri--low'  },
  { key:'media', label:'Media', color:'cal-pri--mid'  },
  { key:'alta',  label:'Alta',  color:'cal-pri--high' },
];
const PRI_MAP = Object.fromEntries(PRIORITIES.map(p=>[p.key,p]));

// fmtDt: usado para timestamps auto-generados por BD (created_at) — interpreta UTC.
const fmtDt = d => fmtSecUTC(d);
// fmtDtLocal: para fechas que escribe el usuario (due_date) — naive local.
const fmtDtLocal = d => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.replace(' ', 'T') : d;
  return new Date(s).toLocaleString('es-ES',{day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit',hour12:false});
};

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayDow(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

// ── TaskDetailModal ───────────────────────────────────────────────────────────

function TaskDetailModal({ task, token, onClose, onUpdated, onDeleted, onOpenLead, onOpenClient }) {
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({
    title: task.title, description: task.description||'',
    due_date: task.dueDate ? task.dueDate.slice(0,16).replace(' ','T') : '',
    priority: task.priority, status: task.status,
    reminder: task.reminder || '',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try { const t = await apiPut(`/tasks/${task.id}`, form, token); onUpdated(t); setEditing(false); }
    catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function toggleStatus() {
    const newStatus = task.status === 'completada' ? 'pendiente' : 'completada';
    try { const t = await apiPut(`/tasks/${task.id}`, { status: newStatus }, token); onUpdated(t); }
    catch (e) { alert(e.message); }
  }

  async function remove() {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    await apiDelete(`/tasks/${task.id}`, token);
    onDeleted(task.id);
  }

  const sf = (k,v) => setForm(p=>({...p,[k]:v}));

  return (
    <div className="cal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={e=>e.stopPropagation()}>
        <div className="cal-modal__head">
          <h3>{editing ? 'Editar tarea' : 'Detalle de tarea'}</h3>
          <button className="cal-btn-x" onClick={onClose}>✕</button>
        </div>
        <div className="cal-modal__body">
          {editing ? (
            <>
              <div className="cal-field"><label>Título *</label><input value={form.title} onChange={e=>sf('title',e.target.value)} autoFocus/></div>
              <div className="cal-field"><label>Descripción</label><textarea rows={3} value={form.description} onChange={e=>sf('description',e.target.value)}/></div>
              <div className="cal-form-2">
                <div className="cal-field"><label>Fecha y hora</label><input type="datetime-local" value={form.due_date} onChange={e=>sf('due_date',e.target.value)}/></div>
                <div className="cal-field"><label>Prioridad</label>
                  <select value={form.priority} onChange={e=>sf('priority',e.target.value)}>
                    {PRIORITIES.map(p=><option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="cal-form-2">
                <div className="cal-field"><label>Estado</label>
                  <select value={form.status} onChange={e=>sf('status',e.target.value)}>
                    <option value="pendiente">Pendiente</option>
                    <option value="completada">Completada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>
                <div className="cal-field"><label>Aviso</label>
                  <select value={form.reminder} onChange={e=>sf('reminder',e.target.value)}>
                    <option value="">Sin aviso</option>
                    <option value="1d">1 día antes</option>
                    <option value="1h">1 hora antes</option>
                    <option value="30m">30 min antes</option>
                    <option value="15m">15 min antes</option>
                    <option value="0m">En el momento</option>
                  </select>
                </div>
              </div>
            </>
          ) : (
            <dl className="cal-dl">
              <dt>Título</dt>     <dd>{task.title}</dd>
              {task.description && <><dt>Descripción</dt><dd>{task.description}</dd></>}
              <dt>Fecha</dt>      <dd>{fmtDtLocal(task.dueDate)}</dd>
              <dt>Prioridad</dt>  <dd><span className={`cal-pri ${PRI_MAP[task.priority]?.color}`}>{PRI_MAP[task.priority]?.label||task.priority}</span></dd>
              <dt>Estado</dt>     <dd><span className={`cal-status cal-status--${task.status}`}>{task.status}</span></dd>
              {task.leadName   && <><dt>Lead</dt>    <dd>{task.leadId   ? <button type="button" className="cal-link" onClick={()=>{onClose(); onOpenLead?.(task.leadId);}}>{task.leadName}</button>     : task.leadName}</dd></>}
              {task.clientName && <><dt>Cliente</dt> <dd>{task.clientId ? <button type="button" className="cal-link" onClick={()=>{onClose(); onOpenClient?.(task.clientId);}}>{task.clientName}</button> : task.clientName}</dd></>}
              {task.reminder && <><dt>Aviso</dt><dd>{{
                '1d':'1 día antes','1h':'1 hora antes','30m':'30 min antes','15m':'15 min antes','0m':'En el momento'
              }[task.reminder]||task.reminder}</dd></>}
              <dt>Creado por</dt> <dd>{task.createdByName}</dd>
              <dt>Creado</dt>     <dd>{fmtDt(task.createdAt)}</dd>
            </dl>
          )}
        </div>
        <div className="cal-modal__foot">
          {editing ? (
            <>
              <button className="cal-btn cal-btn--ghost" onClick={()=>setEditing(false)}>Cancelar</button>
              <button className="cal-btn cal-btn--primary" onClick={save} disabled={saving||!form.title.trim()}>{saving?'…':'Guardar'}</button>
            </>
          ) : (
            <>
              <button className="cal-btn cal-btn--danger-ghost" onClick={remove}>Eliminar</button>
              <button className="cal-btn cal-btn--ghost" onClick={toggleStatus}>
                {task.status==='completada' ? 'Marcar pendiente' : 'Marcar completada'}
              </button>
              <button className="cal-btn cal-btn--primary" onClick={()=>setEditing(true)}>Editar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── NewTaskModal ──────────────────────────────────────────────────────────────

function NewTaskModal({ token, defaultDate, onSave, onClose }) {
  const [form, setForm] = useState({
    title:'', description:'', priority:'media',
    due_date: defaultDate ? `${defaultDate}T09:00` : '',
    reminder: '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const sf = (k,v) => setForm(p=>({...p,[k]:v}));

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('El título es obligatorio'); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="cal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={e=>e.stopPropagation()}>
        <div className="cal-modal__head"><h3>Nueva tarea</h3><button className="cal-btn-x" onClick={onClose}>✕</button></div>
        <form onSubmit={submit} className="cal-modal__body">
          <div className="cal-field"><label>Título *</label><input value={form.title} onChange={e=>sf('title',e.target.value)} autoFocus placeholder="¿Qué hay que hacer?"/></div>
          <div className="cal-field"><label>Descripción</label><textarea rows={2} value={form.description} onChange={e=>sf('description',e.target.value)} placeholder="Detalles opcionales…"/></div>
          <div className="cal-form-2">
            <div className="cal-field"><label>Fecha y hora</label><input type="datetime-local" value={form.due_date} onChange={e=>sf('due_date',e.target.value)}/></div>
            <div className="cal-field"><label>Prioridad</label>
              <select value={form.priority} onChange={e=>sf('priority',e.target.value)}>
                {PRIORITIES.map(p=><option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="cal-field"><label>Aviso / Recordatorio</label>
            <select value={form.reminder} onChange={e=>sf('reminder',e.target.value)}>
              <option value="">Sin aviso</option>
              <option value="1d">1 día antes</option>
              <option value="1h">1 hora antes</option>
              <option value="30m">30 min antes</option>
              <option value="15m">15 min antes</option>
              <option value="0m">En el momento</option>
            </select>
          </div>
          {error && <p className="cal-error">{error}</p>}
          <div className="cal-modal__foot">
            <button type="button" className="cal-btn cal-btn--ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="cal-btn cal-btn--primary" disabled={saving}>{saving?'Creando…':'Crear tarea'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── CalendarPage ──────────────────────────────────────────────────────────────

export default function CalendarPage({ onOpenLead, onOpenClient }) {
  const { auth } = useContext(AuthContext);
  const token = auth.token;

  const openTaskTarget = t => {
    if (t.leadId)   onOpenLead?.(t.leadId);
    else if (t.clientId) onOpenClient?.(t.clientId);
  };

  const [year,    setYear]    = useState(() => new Date().getFullYear());
  const [month,   setMonth]   = useState(() => new Date().getMonth());
  const [tasks,   setTasks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [selTask, setSelTask] = useState(null);
  const [dayModal,setDayModal]= useState(null); // dateKey para mostrar todas las tareas del día
  const [newModal,setNewModal]= useState(null);
  const [fStatus, setFStatus] = useState('');

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ month: month+1, year });
      if (fStatus) p.set('status', fStatus);
      setTasks(await apiGet(`/tasks?${p}`, token));
    } catch {} finally { setLoading(false); }
  }, [token, month, year, fStatus]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  function prevMonth() { if (month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); }
  function nextMonth() { if (month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); }
  function goToday()   { const now = new Date(); setYear(now.getFullYear()); setMonth(now.getMonth()); }

  const daysInMonth = getDaysInMonth(year, month);
  const firstDayDow = getFirstDayDow(year, month);
  const totalCells  = Math.ceil((firstDayDow + daysInMonth) / 7) * 7;

  const tasksByDay = {};
  tasks.forEach(t => {
    if (!t.dueDate) return;
    const key = t.dueDate.slice(0, 10); // "YYYY-MM-DD" directo, sin conversión UTC
    if (!tasksByDay[key]) tasksByDay[key] = [];
    tasksByDay[key].push(t);
  });

  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

  function handleUpdatedTask(updated) { setTasks(p=>p.map(t=>t.id===updated.id?updated:t)); setSelTask(updated); }
  function handleDeletedTask(id)      { setTasks(p=>p.filter(t=>t.id!==id)); setSelTask(null); }
  async function handleCreateTask(form) { const t = await apiPost('/tasks', form, token); setTasks(p=>[...p,t]); }

  const pendientes  = tasks.filter(t=>t.status==='pendiente').length;
  const completadas = tasks.filter(t=>t.status==='completada').length;

  return (
    <div className="cal-page">

      {/* Header */}
      <div className="cal-header">
        <div className="cal-header-left">
          <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
          <h2 className="cal-title">{MONTH_NAMES[month]} {year}</h2>
          <button className="cal-nav-btn" onClick={nextMonth}>›</button>
          <button className="cal-today-btn" onClick={goToday}>Hoy</button>
        </div>
        <div className="cal-header-right">
          <div className="cal-legend">
            <span className="cal-leg cal-leg--pend">{pendientes} pendiente{pendientes!==1?'s':''}</span>
            <span className="cal-leg cal-leg--done">{completadas} completada{completadas!==1?'s':''}</span>
          </div>
          <select className="cal-sel" value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="">Todas</option>
            <option value="pendiente">Pendientes</option>
            <option value="completada">Completadas</option>
            <option value="cancelada">Canceladas</option>
          </select>
          <button className="cal-btn cal-btn--primary" onClick={()=>setNewModal('')}>+ Nueva tarea</button>
        </div>
      </div>

      {/* Cuadrícula */}
      {loading ? (
        <div className="cal-loading">Cargando tareas…</div>
      ) : (
        <div className="cal-grid">
          {DAY_NAMES.map(d=>(
            <div key={d} className="cal-dow">{d}</div>
          ))}
          {Array.from({length: totalCells}, (_,i) => {
            const dayNum = i - firstDayDow + 1;
            const isCurrentMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const dateKey = isCurrentMonth
              ? `${year}-${String(month+1).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`
              : null;
            const isToday = dateKey === todayKey;
            const dayTasks = (dateKey && tasksByDay[dateKey]) || [];

            return (
              <div
                key={i}
                className={`cal-cell ${isCurrentMonth?'cal-cell--cur':'cal-cell--out'} ${isToday?'cal-cell--today':''}`}
                onClick={() => isCurrentMonth && setNewModal(dateKey)}
              >
                {isCurrentMonth && (
                  <>
                    <div className="cal-daynum">{dayNum}</div>
                    <div className="cal-chips">
                      {dayTasks.slice(0,3).map(t=>(
                        <div
                          key={t.id}
                          className={`cal-chip cal-chip--${t.status} cal-chip--pri-${t.priority}`}
                          onClick={e=>{e.stopPropagation();setSelTask(t);}}
                          title={t.title}
                        >
                          <span className="cal-chip-dot"/>
                          <span className="cal-chip-txt">{t.title}</span>
                          {t.leadName && (
                            (t.leadId || t.clientId)
                              ? <span className="cal-chip-sub cal-link" onClick={e=>{e.stopPropagation(); openTaskTarget(t);}}>{t.leadName}</span>
                              : <span className="cal-chip-sub">{t.leadName}</span>
                          )}
                        </div>
                      ))}
                      {dayTasks.length>3 && (
                        <div className="cal-chip-more" onClick={e=>{e.stopPropagation();setDayModal(dateKey);}}>
                          +{dayTasks.length-3} más
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tareas sin fecha */}
      {tasks.filter(t=>!t.dueDate).length > 0 && (
        <div className="cal-no-date">
          <div className="cal-no-date__ttl">Sin fecha asignada</div>
          {tasks.filter(t=>!t.dueDate).map(t=>(
            <div key={t.id} className={`cal-task-row cal-task-row--${t.status}`} onClick={()=>setSelTask(t)}>
              <span className={`cal-chip-dot cal-dot--pri-${t.priority}`}/>
              <span className="cal-task-row__title">{t.title}</span>
              {t.leadName && (
                (t.leadId || t.clientId)
                  ? <span className="cal-task-row__sub cal-link" onClick={e=>{e.stopPropagation(); openTaskTarget(t);}}>{t.leadName}</span>
                  : <span className="cal-task-row__sub">{t.leadName}</span>
              )}
              <span className={`cal-status cal-status--${t.status}`}>{t.status}</span>
            </div>
          ))}
        </div>
      )}

      {selTask && (
        <TaskDetailModal task={selTask} token={token} onClose={()=>setSelTask(null)}
          onUpdated={handleUpdatedTask} onDeleted={handleDeletedTask}
          onOpenLead={onOpenLead} onOpenClient={onOpenClient}/>
      )}
      {newModal !== null && (
        <NewTaskModal token={token} defaultDate={newModal} onSave={handleCreateTask} onClose={()=>setNewModal(null)}/>
      )}

      {/* Modal día completo — muestra todas las tareas de un día */}
      {dayModal && (
        <div className="cal-overlay" onClick={()=>setDayModal(null)}>
          <div className="cal-modal" onClick={e=>e.stopPropagation()}>
            <div className="cal-modal__head">
              <h3>Tareas del {new Date(dayModal+'T12:00').toLocaleDateString('es-ES',{day:'2-digit',month:'long',year:'numeric'})}</h3>
              <button className="cal-btn-x" onClick={()=>setDayModal(null)}>✕</button>
            </div>
            <div className="cal-modal__body" style={{maxHeight:'60vh',overflowY:'auto'}}>
              {(tasksByDay[dayModal]||[]).length === 0 ? (
                <p style={{color:'#94a3b8',textAlign:'center',padding:'1rem 0'}}>No hay tareas este día.</p>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {(tasksByDay[dayModal]||[]).map(t => (
                    <div key={t.id}
                      style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,border:'1px solid #e2e8f0',cursor:'pointer',transition:'background .12s'}}
                      onClick={()=>{setDayModal(null);setSelTask(t);}}
                      onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                    >
                      <span style={{width:8,height:8,borderRadius:'50%',flexShrink:0,background:t.priority==='alta'?'#ef4444':t.priority==='media'?'#f59e0b':'#94a3b8'}}/>
                      <div style={{flex:1,overflow:'hidden'}}>
                        <div style={{fontSize:'.88rem',fontWeight:600,color:'#1a1a2e',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{t.title}</div>
                        {(t.leadName||t.clientName) && (
                          (t.leadId || t.clientId)
                            ? <div className="cal-link" style={{fontSize:'.75rem'}} onClick={e=>{e.stopPropagation(); setDayModal(null); openTaskTarget(t);}}>{t.leadName||t.clientName}</div>
                            : <div style={{fontSize:'.75rem',color:'#94a3b8'}}>{t.leadName||t.clientName}</div>
                        )}
                      </div>
                      <span style={{fontSize:'.72rem',fontWeight:600,padding:'2px 8px',borderRadius:20,background:t.status==='completada'?'#dcfce7':'#f1f5f9',color:t.status==='completada'?'#16a34a':'#64748b'}}>{t.status}</span>
                      {t.dueDate && <span style={{fontSize:'.72rem',color:'#94a3b8'}}>{String(t.dueDate).slice(11,16)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
